import { Location, CurrencyPipe, DatePipe } from '@angular/common'
import {
   Component,
   HostBinding,
   Injectable,
   Input,
   OnInit,
   ViewChild,
   ChangeDetectorRef,
   ElementRef,
   inject,
   EventEmitter,
   computed,
   model,
   signal,
   effect,
} from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { LazyLoadEvent } from 'primeng/api'
import { Sort } from '@angular/material/sort'
import { MatTableDataSource } from '@angular/material/table'
import { MatSort } from '@angular/material/sort'
import { ToastrService } from 'ngx-toastr'
import { BehaviorSubject, EMPTY, Subject, combineLatest, forkJoin, of } from 'rxjs'
import { VetcorPricingTableComponent } from '../vetcor-pricing-table/vetcor-pricing-table.component'
import { catchError, map, switchMap, take, tap } from 'rxjs/operators'
import { AuthManager } from 'src/app/shared/auth/AuthManager'
import {
   AppPractice,
   PricingManagerClient,
   PracticeBatchInfo,
   PracticesClient,
   FileParameter,
   PricingCycleUpdate,
} from 'src/app/shared/services/ppv.api'
import { ComponentStore } from '@ngrx/component-store'
import { VetcorPricingRowService } from '../vetcor-pricing-table/vetcor-pricing-row.service'
import { PriceActionService } from 'src/app/shared/services/pricing-actions.service'
import { BsModalService } from 'ngx-bootstrap/modal'
import { VetcorPracticeSettingsModalComponent } from '../practice-settings-modal/practice-settings-modal.component'
import { VetcorUploadCsvModalComponent } from './pricing-admin-upload-csv-modal/pricing-admin-upload-csv-modal.component'
import { VetcorUploadDbxModalComponent } from '../upload-dbx-modal/upload-dbx-modal.component'
import { CsvFileType, UploadCsvEvent, ValidateCsvRequest } from 'src/app/shared/models/csv/validate-csv'
import { IDropdownOption } from 'src/app/shared/models/dropdown-option.interface'

@Injectable()
export class VetcorPricingAdminComponentStore extends ComponentStore<{
   selectedPracticeSKey?: number
   selectedBatch?: PracticeBatchInfo
   loadingPIMS: boolean
   searchText: string
   searchCode: string
   selectedCategory: string
}> {
   constructor() {
      super({
         loadingPIMS: false,
         searchText: '',
         searchCode: '',
         selectedCategory: '',
      })
   }
}

interface FileError {
   index: string
   message: string
}

@Component({
   selector: 'vetcor-pricing-admin',
   templateUrl: './vetcor-pricing-admin.component.html',
   styleUrls: ['./vetcor-pricing-admin.component.scss'],
   providers: [VetcorPricingAdminComponentStore, CurrencyPipe, DatePipe, VetcorPricingRowService, PriceActionService],
   standalone: false,
})
export class VetcorPricingAdminComponent implements OnInit {
   @ViewChild(VetcorPricingTableComponent)
   childTable!: VetcorPricingTableComponent

   practicesApi = inject(PracticesClient)
   savedList: boolean = false
   savePDFClicked: boolean = false
   showOnlyUnreviewedProducts: boolean = false

   isCaptain: boolean = true
   loading = true
   practicesLookup$ = new BehaviorSubject<{
      [practiceSKey: number]: AppPractice
   }>(undefined)
   practices: AppPractice[] = []
   refreshBatchStats$ = new Subject<void>()

   adminDashboard: number = 1
   selectedCategory = ''
   products: any[] = []
   productsSub = new BehaviorSubject<any[]>([])
   dataSource: MatTableDataSource<PricingManagerClient> = new MatTableDataSource()
   @ViewChild(MatSort) sort!: MatSort
   public filteredProducts: Array<any> = []

   @ViewChild('rationaleInput') rationaleInput: ElementRef
   approveAllPrices = true
   siteOptions: any[] = [] // To store available site options for a selected practice
   selectedSite: any = null

   public pricingCaptain: Array<any> = []
   public codeFilterValue: string = ''
   public productNameFilter: string = ''
   public productTypeFilter: string = ''
   public productCategoryFilter: string = ''
   public totalSuggestedPrice: any
   public practiceCompletion: number = 0
   public codesPendingWriteback: number = 0
   public codesRequestedReview: number = 0
   public practiceCompletionAdmin: number = 0

   //settingsForPractice: PracticeBatchInfo[] = [];
   settingsForPractice = signal<PracticeBatchInfo>(null)

   public productCategories: Array<{ label: string; value: string | null }> = []

   statusOptions = [
      { label: 'No Feedback Received', value: 0 },
      { label: 'Feedback Started', value: 1 },
      { label: 'Pending Review', value: 2 },
      { label: 'Submitted', value: 3 },
   ]

   selectedPricingPartner: number | null = null
   pricingPartnerOptions: {
      pricingCaptainUserId: number
      workEmail: string
      displayName: string
   }[] = []

   sortValue = model<Sort>({ active: 'practiceName', direction: 'asc' })
   dashboardStats: any = {}
   upcomingPractices: any[] = []
   unsubmittedPractices: any[] = []
   writebackSuccessPractices: any[] = []
   allPractices = signal<PracticeBatchInfo[]>([])
   allPracticesForExport: PracticeBatchInfo[] = []

   filteredPractices = computed(() => {
      console.log('Filtering practices')
      const searchTermLower = this.practiceSearchTerm()?.trim().toLowerCase() || ''
      let filteredPractices = this.allPractices()?.filter((practice) => practice.practiceName?.toLowerCase().includes(searchTermLower))

      const sort = this.sortValue()
      if (!sort.active || sort.direction === '') {
         return filteredPractices
      } else {
         filteredPractices = filteredPractices.sort((a, b) => {
            let aValue: any
            let bValue: any

            if (sort.active === 'writebackSuccess') {
               if (a.batchStatus === 3 && new Date(a.scheduledFor) <= new Date()) {
                  aValue = a.writebackSuccess
               } else {
                  aValue = null
               }

               if (b.batchStatus === 3 && new Date(b.scheduledFor) <= new Date()) {
                  bValue = b.writebackSuccess
               } else {
                  bValue = null
               }
            } else {
               aValue = a[sort.active]
               bValue = b[sort.active]
            }

            if (aValue == null) return 1
            if (bValue == null) return -1

            if (typeof aValue === 'string' && typeof bValue === 'string') {
               return aValue.localeCompare(bValue) * (sort.direction === 'asc' ? 1 : -1)
            }

            if (typeof aValue === 'number' && typeof bValue === 'number') {
               return (aValue - bValue) * (sort.direction === 'asc' ? 1 : -1)
            }

            return 0
         })
      }

      return filteredPractices
   })

   isLoadingForReview = true
   isLoadingCompleted = true
   isLoadingAllPractices = true
   isExportingCSV = false
   practiceSKeyNumber = 0
   hmFirstName = ''
   hmLastName = ''
   hmEmail = ''
   hmPhone = ''
   chiefFirstName = ''
   chiefLastName = ''
   batchId = 0
   batchStatus = 0
   discussionAvailable: Date | null = null
   discussionComplete: Date | null = null

   hasProSal = ''

   practiceSearchTerm = model<string>(undefined)

   isAdmin: boolean = false
   isReadOnly: boolean = false
   currentDateTime = new Date()
   currentAdmin = ''
   currentPosition = ''

   @Input() statusProgress: number = 0
   //@Input() statusProgress = signal<number>(0)
   strokeDashArray: string = '0'
   statusLabel: string = 'Excellent'
   statusColor: string = '#01a978'

   selectedComment: string | null = null // To hold the selected value

   selectedPractice$ = combineLatest([this.store.select((s) => s.selectedPracticeSKey), this.practicesLookup$]).pipe(
      map(([selectedPracticeSKey, practiceLookup]) => (practiceLookup ? practiceLookup[selectedPracticeSKey] : undefined))
   )

   state$ = combineLatest([this.store.select((s) => s), this.selectedPractice$]).pipe(
      map(([state, selectedPractice]) => ({
         ...state,
         selectedPractice,
      }))
   )

   passedScheduleDate: boolean = false

   onResetBatch = new Subject<PracticeBatchInfo>()
   onDateChange = new Subject<any>()
   onValueChange = new Subject<any>()
   onPricingPartnerChange = new Subject<any>()

   @HostBinding('class.full-flex-height') fullFlexHeight = true

   get practiceCompletionPercent(): string {
      return `${this.practiceCompletion}%`
   }

   onUploadCsv = new EventEmitter<UploadCsvEvent>()
   onSelectPricingFile = new EventEmitter<ValidateCsvRequest>()
   onSelectRuleSetsFile = new EventEmitter<ValidateCsvRequest>()
   onSelectFile = new EventEmitter<ValidateCsvRequest>()
   isSubmittingCsv = new BehaviorSubject<boolean>(false)
   isSubmittingDbx = new BehaviorSubject<{ source: string; pipeline: string }>({ source: '', pipeline: '' })
   isValidating = new BehaviorSubject<boolean>(false)
   csvErrors = new BehaviorSubject<FileError[]>([])
   csvWarnings = new BehaviorSubject<FileError[]>([])
   pricingImportDestinationTable = 'PPVApps.[dbo].[Import_PricingUpdateRecord]'

   public displayTagFilter = model<string>('')

   public displayTags = signal<string[]>(undefined)
   public displayTagOptions = computed(() => {
      return this.displayTags()
         ?.filter((tag) => !!tag)
         .map((category) => ({
            label: category,
            value: category,
         }))
   })

   constructor(
      private authManager: AuthManager,
      private route: ActivatedRoute,
      private location: Location,
      private store: VetcorPricingAdminComponentStore,
      private pricingManagerClient: PricingManagerClient,
      private toastr: ToastrService,
      private cdr: ChangeDetectorRef,
      private currencyPipe: CurrencyPipe,
      private datePipe: DatePipe,
      private pricingService: VetcorPricingRowService,
      private priceActionService: PriceActionService,
      private bsModalService: BsModalService,
      public router: Router
   ) {
      this.productsSub.subscribe((products) => {
         this.products = products
      })
      this.onSelectPricingFile.subscribe((val) => {
         const { fileParameter, expectedHeaders } = val
         this.validatePricesCsv(fileParameter, expectedHeaders)
      })
      this.onSelectRuleSetsFile.subscribe((val) => {
         const { fileParameter, expectedHeaders } = val
         this.validateRuleSetsCsv(fileParameter, expectedHeaders)
      })
      this.onSelectFile.subscribe((val) => {
         const { fileParameter, expectedHeaders, fileType } = val
         switch (fileType) {
            case CsvFileType.HospitalPricing:
               this.validatePricesCsv(fileParameter, expectedHeaders)
               break
            case CsvFileType.BatchUpdates:
               this.validateBatchUpdatesCsv(fileParameter, expectedHeaders)
               break
            case CsvFileType.RuleSets:
               this.validateRuleSetsCsv(fileParameter, expectedHeaders)
               break
            case CsvFileType.Rules:
               this.validateRulesCsv(fileParameter, expectedHeaders)
               break
            default:
               this.invalidateFile(fileParameter)
               break
         }
      })
      this.isSubmittingDbx.subscribe((val) => {
         const { source, pipeline } = val
         if (source?.length > 0) {
            this.uploadPricingUpdatesViaADF(source, pipeline)
         }
      })
      this.onUploadCsv.subscribe((uploadEvent) => this.uploadData(uploadEvent))

      this.pricingManagerClient
         .getDisplayTags()
         .pipe(
            take(1),
            tap((tags) => this.displayTags.set(tags))
         )
         .subscribe()

      effect(() => {
         if (this.displayTagFilter()) {
            this.fetchAllPractices()
         }
      })
   }

   ngOnInit(): void {
      this.onResetBatch.subscribe((val) => {
         this.resetBatch(val)
      })
      this.onDateChange.subscribe((val) => {
         const { newDate, column } = val
         this.dateChange(newDate, column)
      })
      this.onValueChange.subscribe((val) => {
         const { newValue, column } = val
         this.valueChange(newValue, column)
      })
      this.onPricingPartnerChange.subscribe((val) => {
         if (val.newValue && val.newValue.displayName != null && val.newValue.pricingCaptainUserId != null) {
            const { displayName, pricingCaptainUserId } = val.newValue
            this.pricingPartnerChange(displayName, pricingCaptainUserId)
         }
      })

      const practiceSKey = this.route.snapshot.paramMap.get('practiceSKey')
      this.isAdmin = false

      this.store.setState({
         selectedPracticeSKey: !!practiceSKey ? Number.parseInt(practiceSKey) : undefined,
         selectedBatch: undefined,
         loadingPIMS: false,
         searchText: '',
         searchCode: '',
         selectedCategory: '',
      })

      this.authManager
         .getCurrentUser()
         .pipe(
            take(1),
            tap((user) => {
               var adminRole = user.roles.find((x) => {
                  return x.roleName == 'PricingManagerAdmin'
               })
			   var rmRole = user.roles.find((x) => {
                  return x.roleName == 'RM'
               })
			   var hmRole = user.roles.find((x) => {
                  return x.roleName == 'HospitalManager'
               })
               if (adminRole) {
                  this.isAdmin = true
				  this.currentPosition = 'Pricing Admin'
               } else if (rmRole) {
			      this.currentPosition = 'Regional Manager'
			   } else if (hmRole) {
			      this.currentPosition = 'Hospital Manager'
			   }

               this.currentAdmin = user.firstName + ' ' + user.lastName

               this.updateReadOnly()

               this.practices = user.practices.filter((p) => !p.doesNotSync)
               const newLookup = {}
               this.practices.forEach((prac) => {
                  newLookup[prac.id] = prac
               })
               this.practicesLookup$.next(newLookup)
               if (this.practices.length == 1) {
                  this.onPracticeSelected(this.practices[0])
               }
            })
         )
         .subscribe()

      if (!!practiceSKey) {
         this.practiceSKeyNumber = Number(practiceSKey)
         this.getPracticeDetails(this.practiceSKeyNumber)
         this.getDocComp(this.practiceSKeyNumber)
      }

      /*
      Turning this off until 2026
      this.refreshBatchStats$
         .pipe(
            switchMap(() => this.store.select((s) => s.selectedPracticeSKey)),
            switchMap((practiceSKey) => this.pricingManagerClient.getBatchInfo(practiceSKey)),
            tap((batchInfo) => {
               let practices = this.filteredPractices
               practices = practices.map((p) => {
                  if (p.practiceSKey == batchInfo?.practiceSKey) {
                     return { ...p, ...batchInfo }
                  } else return p
               })

               let allPractices = this.allPractices
               allPractices = allPractices.map((p) => {
                  if (p.practiceSKey == batchInfo?.practiceSKey) {
                     return { ...p, ...batchInfo }
                  } else return p
               })

               this.allPractices = allPractices
               this.filteredPractices = practices
               this.store.patchState({
                  selectedBatch: batchInfo,
               })
            })
         )
         .subscribe()
         */
   }

   hideDotsDrop(dropdownElement: HTMLElement): void {
      if (dropdownElement.classList.contains('show')) {
         dropdownElement.classList.remove('show')
      }
   }

   openDetailsModal(practiceSKey: number): void {
      document.getElementById('detailsModalButton').click()
   }

   updateProgress(): void {
      const radius = 15.9155
      const circumference = 2 * Math.PI * radius

      const filled = (this.statusProgress / 100) * circumference
      const remaining = circumference - filled

      this.strokeDashArray = `${this.statusProgress}`

      if (this.statusProgress >= 80) {
         this.statusLabel = 'Excellent'
         this.statusColor = '#01a978' // Green
      } else if (this.statusProgress >= 60) {
         this.statusLabel = 'Good'
         this.statusColor = '#ffc107' // Yellow
      } else if (this.statusProgress >= 1) {
         this.statusLabel = 'Poor'
         this.statusColor = '#d31b31' // Red
      } else {
		 this.statusLabel = 'Not Started'
         this.statusColor = '#a2a2a2' // Gray
	  }
   }

   getStatusBackgroundColor(value: number): string {
      if (value == 1) {
         return '#37b08d'
      } else if (value >= 0.6) {
         return '#ffbc34'
      } else {
         return '#d41a31'
      }
   }

   getStatusBackgroundColorClass(value: number): string {
      if (value == 1) {
         return 'status-good'
      } else if (value >= 0.6) {
         return 'status-weak'
      } else {
         return 'status-poor'
      }
   }

   resetBatch(settingsForPractice: any): void {
      if (this.selectedPricingPartner !== null) {
         settingsForPractice.pricingCaptainUserId = this.selectedPricingPartner
      }
      this.pricingManagerClient.resetBatch(settingsForPractice).subscribe({
         next: (response) => {
            console.log('Reset batch info for practice')
            this.fetchAllPractices()
         },
         error: (error) => {
            console.error('Error resetting batch:', error)
         },
      })
   }

   dateChange(newDate: string, column: string): void {
      const fullDate = new Date(newDate)
      const isoDate = newDate

      if (column == 'discussionAvailable') {
         this.settingsForPractice.set({
            ...this.settingsForPractice(),
            discussionAvailable: new Date(new Date(`${isoDate}T00:00:00Z`).toISOString()),
         })
      } else {
         this.settingsForPractice.set({ ...this.settingsForPractice(), [column]: new Date(new Date(`${isoDate}T23:59:00Z`).toISOString()) })
      }
   }

   valueChange(newValue: string | number, column: string): void {
      this.settingsForPractice.set({ ...this.settingsForPractice(), [column]: newValue })
   }

   pricingPartnerChange(displayName: string, pricingCaptainUserId: number): void {
      this.settingsForPractice.set({ ...this.settingsForPractice(), pricingPartner: displayName, pricingCaptainUserId: pricingCaptainUserId })
   }

   openSettings(settingsForPractice: PracticeBatchInfo): void {
      this.settingsForPractice.set({ ...settingsForPractice })
      this.bsModalService.show(VetcorPracticeSettingsModalComponent, {
         class: 'custom-modal pricing-settings-modal',
         backdrop: 'static',
         initialState: {
            settingsForPractice: this.settingsForPractice,
            onResetBatch: this.onResetBatch,
            statusOptions: this.statusOptions,
            pricingPartnerOptions: this.pricingPartnerOptions,
            selectedPricingPartner: this.selectedPricingPartner,
            onDateChange: this.onDateChange,
            onValueChange: this.onValueChange,
            onPricingPartnerChange: this.onPricingPartnerChange,
         },
      })
   }

   getFormattedPct(value: number): string {
      return (value * 100).toFixed(1) + '%'
   }

   scrollUp(): void {
      ;(window as any).scrollTo({ top: 0, behavior: 'smooth' })
   }

   public isHidden: boolean[] = [false]
   toggleVisibility(index: number) {
      this.isHidden[index] = !this.isHidden[index]
   }

   trackByFn(index: number, item: any): number {
      return item.practice
   }

   loadMore($event: LazyLoadEvent) {}

   onPracticeSelected(practice: AppPractice) {
      if (!!practice) {
         this.practiceSKeyNumber = practice.id
         this.router.navigateByUrl(String(this.practiceSKeyNumber))
         //this.fetchPracticeLiveDataInfo(this.practiceSKeyNumber);
         this.getPracticeDetails(this.practiceSKeyNumber)
         this.getDocComp(this.practiceSKeyNumber)
      } else {
         this.location.go('/vetcor-pricing/admin')
      }

      this.store.patchState({
         selectedPracticeSKey: this.practiceSKeyNumber,
      })
   }

   fetchDashboardStats(): void {
      this.pricingManagerClient.getDashboardStats(this.displayTagFilter()).subscribe((data) => {
         this.dashboardStats = data
         const tcalc = data.writebackFailed / data.writebackTotal
         const tcalc2 = (data.writebackFailed + data.completedWriteback) / data.writebackTotal

         if (data.totalPractices == 0) {
            this.statusProgress = 100 - 0
            setTimeout(() => {
               const tcalcAdmin = this.practiceCompletionAdmin / this.allPractices().length
               this.practiceCompletion = Math.round(tcalcAdmin * 100)
            }, 800)
         } else {
            this.statusProgress = (1 - tcalc) * 100
            this.practiceCompletion = Math.round(tcalc2 * 100)
         }

         this.updateProgress()
      })
   }

   fetchAllPractices(): void {
      if (!this.displayTagFilter()) {
         console.error('Display tag filter is required to fetch practices.')
         return
      }

      this.allPractices.set(undefined)
      this.pricingManagerClient.getPricingCaptainPractices(this.displayTagFilter()).subscribe((data) => {
         data.forEach((practice) => {
            if (practice.waveStart) {
               practice.waveStart = new Date(practice.waveStart)
            }
         })
         this.allPractices.set(data)
         this.pricingPartnerOptions = data
            .reduce((acc, p) => {
               if (!acc.find((x) => x.displayName === p.pricingPartner)) {
                  acc.push({
                     pricingCaptainUserId: p.pricingCaptainUserId,
                     workEmail: '',
                     displayName: p.pricingPartner,
                  })
               }
               return acc
            }, [] as { pricingCaptainUserId: number; workEmail: string; displayName: string }[])
            .filter((option) => option.displayName)
            .sort((a, b) => a.displayName.localeCompare(b.displayName))

         this.unsubmittedPractices = [
            ...this.allPractices().filter(
               (practice) => (practice.batchStatus === 0 || practice.batchStatus === 1) && practice.waveStart <= new Date()
            ),
         ]
         this.upcomingPractices = [...this.allPractices().filter((practice) => practice.batchStatus === 2)]
         this.writebackSuccessPractices = [
            ...this.allPractices().filter((practice) => practice.batchStatus === 3 && new Date(practice.scheduledFor) <= new Date()),
         ]

         this.isLoadingAllPractices = false

         this.codesRequestedReview = data.reduce((sum, record) => sum + (record.codesForReviewCount || 0), 0)
         this.codesPendingWriteback = data.reduce((sum, record) => sum + (record.codesForWritebackCount || 0), 0)
         this.practiceCompletionAdmin = data.filter((item: any) => item.batchStatus === 3).length

         this.fetchDashboardStats()
      })
   }

   fetchAllPracticesForExport(): void {
      this.pricingManagerClient.getPricingCaptainPracticesForExport(this.displayTagFilter()).subscribe((data) => {
         data.forEach((practice) => {
            if (practice.waveStart) {
               practice.waveStart = new Date(practice.waveStart)
            }
         })
         this.allPracticesForExport = data

         this.exportData()
      })
   }

   hasStartedWriteback(practice: any): boolean {
      if (!practice || !practice.scheduledFor) {
         return false
      }

      const scheduledDate = new Date(practice.scheduledFor)
      return practice.batchStatus === 3 && scheduledDate <= new Date()
   }

   getPracticeDetails(practiceSKey: number) {
      forkJoin([this.practicesApi.getPractice(practiceSKey), this.pricingManagerClient.getBatchInfo(practiceSKey)])
         .pipe(
            tap(([practice, batchInfo]) => {
               const hm = practice?.hMs && practice.hMs.length > 0 ? practice.hMs[0] : null

               this.hmFirstName = hm?.firstName
               this.hmLastName = hm?.lastName
               this.hmEmail = hm?.workEmail
               this.hmPhone = practice?.phone
               this.chiefFirstName = practice?.cos?.firstName
               this.chiefLastName = practice?.cos?.lastName
               this.batchId = batchInfo?.batchId
               this.batchStatus = batchInfo?.batchStatus
               this.discussionAvailable = batchInfo?.discussionAvailable
               this.discussionComplete = batchInfo?.discussionComplete
               this.updateReadOnly()

               this.store.patchState({
                  selectedBatch: batchInfo,
               })
            }),
            take(1)
         )
         .subscribe()
   }

   public getDocComp(PracticeSKey: number) {
      this.pricingManagerClient.getDocComp(PracticeSKey).subscribe(
         (data) => {
            this.hasProSal = data
         },
         (error) => {
            console.error('Error fetching practice name:', error)
         }
      )
   }

   updateReadOnly() {
      this.isReadOnly = !this.isAdmin && this.batchStatus === 3
   }

   onRecordUpdated(): void {
      this.cdr.detectChanges()
      this.refreshBatchStats$.next()
   }

   exportTablePDF() {
      setTimeout(() => {
         if (this.childTable) {
            this.childTable.exportTableToPDF('captain', this.filteredProducts)
         }
      }, 200)
   }

   onProductListSaved(saved: boolean) {
      this.savedList = saved
   }

   exportDataClicked(): void {
      this.isExportingCSV = true

      this.fetchAllPracticesForExport()
   }

   closePopup(): void {
      this.isExportingCSV = false
   }

   exportData(): void {
      const csvData = this.allPracticesForExport.map((practice) => {
         return {
            practiceName: practice.practiceName,
            hm: practice.hmFirstName + ' ' + practice.hmLastName,
            hmEmail: practice.hmEmail,
            practiceEmail: practice.practiceEmail,
            practicePhone: practice.practicePhone,
            batchStatus:
               practice.batchStatus === 0
                  ? 'No Feedback Received'
                  : practice.batchStatus === 1
                  ? 'Feedback Started'
                  : practice.batchStatus === 2
                  ? 'Pending Review'
                  : 'Submitted',
            discussionComplete: this.datePipe.transform(practice.discussionComplete, 'MM/dd/yyyy'),
            scheduledFor: this.datePipe.transform(practice.scheduledFor, 'MM/dd/yyyy'),
            currentLTMRevenue: this.currencyPipe.transform(practice.currentLTMRevenue, 'USD', 'symbol', '1.0-0'),

            //impact $
            revenueImpact: this.currencyPipe.transform(practice.revenueImpact, 'USD', 'symbol', '1.0-0'),
            requestedRevenueImpact: this.currencyPipe.transform(practice.requestedRevenueImpact, 'USD', 'symbol', '1.0-0'),
            finalRevenueImpact: this.currencyPipe.transform(practice.finalRevenueImpact, 'USD', 'symbol', '1.0-0'),

            //impact %
            revenueImpactPct: this.getFormattedPct(practice.revenueImpactPct),
            requestedRevenueImpactPct: this.getFormattedPct(practice.requestedRevenueImpactPct),
            finalRevenueImpactPct: this.getFormattedPct(practice.finalRevenueImpactPct),

            //average change $
            modeledAverageChange: this.currencyPipe.transform(practice.modeledAverageChange, 'USD', 'symbol', '1.1-1'),
            requestedAverageChange: this.currencyPipe.transform(practice.requestedAverageChange, 'USD', 'symbol', '1.1-1'),
            finalAverageChange: this.currencyPipe.transform(practice.finalAverageChange, 'USD', 'symbol', '1.1-1'),

            //average change %
            modeledAverageChangePct: this.getFormattedPct(practice.modelAverageChangePct),
            requestedAverageChangePct: this.getFormattedPct(practice.requestedAverageChangePct),
            finalAverageChangePct: this.getFormattedPct(practice.finalAverageChangePct),

            //weighted change $
            modeledWeightedImpactChange: this.currencyPipe.transform(practice.modeledWeightedImpactChange, 'USD', 'symbol', '1.1-1'),
            requestedWeightedImpactChange: this.currencyPipe.transform(practice.requestedWeightedImpactChange, 'USD', 'symbol', '1.1-1'),
            finalWeightedImpactChange: this.currencyPipe.transform(practice.weightedImpactChange, 'USD', 'symbol', '1.1-1'),

            //weighted change %
            modeledWeightedImpactChangePct: this.getFormattedPct(practice.modeledWeightedImpactChangePct),
            requestedWeightedImpactChangePct: this.getFormattedPct(practice.requestedWeightedImpactChangePct),
            finalWeightedImpactChangePct: this.getFormattedPct(practice.weightedImpactChangePct),

            //prod and svc $ impact
            productsModeledRevenueImpact: this.currencyPipe.transform(practice.productsModeledRevenueImpact, 'USD', 'symbol', '1.1-1'),
            productsRequestedRevenueImpact: this.currencyPipe.transform(practice.productsRequestedRevenueImpact, 'USD', 'symbol', '1.1-1'),
            productsFinalRevenueImpact: this.currencyPipe.transform(practice.productsFinalRevenueImpact, 'USD', 'symbol', '1.1-1'),
            servicesModeledRevenueImpact: this.currencyPipe.transform(practice.servicesModeledRevenueImpact, 'USD', 'symbol', '1.1-1'),
            servicesRequestedRevenueImpact: this.currencyPipe.transform(practice.servicesRequestedRevenueImpact, 'USD', 'symbol', '1.1-1'),
            servicesFinalRevenueImpact: this.currencyPipe.transform(practice.servicesFinalRevenueImpact, 'USD', 'symbol', '1.1-1'),

            //prod and svc % impact
            productsModeledRevenueImpactPct: this.getFormattedPct(practice.productsModeledRevenueImpactPct),
            productsRequestedRevenueImpactPct: this.getFormattedPct(practice.productsRequestedRevenueImpactPct),
            productsFinalRevenueImpactPct: this.getFormattedPct(practice.productsFinalRevenueImpactPct),
            servicesModeledRevenueImpactPct: this.getFormattedPct(practice.servicesModeledRevenueImpactPct),
            servicesRequestedRevenueImpactPct: this.getFormattedPct(practice.servicesRequestedRevenueImpactPct),
            servicesFinalRevenueImpactPct: this.getFormattedPct(practice.servicesFinalRevenueImpactPct),

            //prod and svc avg price change %
            productsModeledAverageChangePct: this.getFormattedPct(practice.productsModeledAverageChangePct),
            productsRequestedAverageChangePct: this.getFormattedPct(practice.productsRequestedAverageChangePct),
            productsFinalAverageChangePct: this.getFormattedPct(practice.productsFinalAverageChangePct),
            servicesModeledAverageChangePct: this.getFormattedPct(practice.servicesModeledAverageChangePct),
            servicesRequestedAverageChangePct: this.getFormattedPct(practice.servicesRequestedAverageChangePct),
            servicesFinalAverageChangePct: this.getFormattedPct(practice.servicesFinalAverageChangePct),

            //prod and svc wtd avg price change %
            productsModeledWeightedImpactChangePct: this.getFormattedPct(practice.productsModeledWeightedImpactChangePct),
            productsRequestedWeightedImpactChangePct: this.getFormattedPct(practice.productsRequestedWeightedImpactChangePct),
            productsFinalWeightedImpactChangePct: this.getFormattedPct(practice.productsFinalWeightedImpactChangePct),
            servicesModeledWeightedImpactChangePct: this.getFormattedPct(practice.servicesModeledWeightedImpactChangePct),
            servicesRequestedWeightedImpactChangePct: this.getFormattedPct(practice.servicesRequestedWeightedImpactChangePct),
            servicesFinalWeightedImpactChangePct: this.getFormattedPct(practice.servicesFinalWeightedImpactChangePct),
         }
      })

      const csvContent = this.convertToCSV(csvData)

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = 'pricingManager_practices.csv'
      link.click()

      this.isExportingCSV = false
   }

   private convertToCSV(objArray: any[]): string {
      const escapeCSVValue = (value: string | number | null | undefined): string => {
         if (value === null || value === undefined) {
            return ''
         }
         if (typeof value === 'string') {
            return `"${value.replace(/"/g, '""')}"`
         }
         return value.toString()
      }

      const array = [
         [
            'Practice Name',
            'Hospital Manager',
            'HM Email',
            'Practice Phone',
            'Practice Status',
            'Deadline for Reviews',
            'PIMS Writeback',
            'L12M Rev. Baseline',

            'Modeled $ Impact',
            'Requested $ Impact',
            'Final $ Impact',

            'Modeled % Impact',
            'Requested % Impact',
            'Final % Impact',

            'Modeled Avg $ Price Change',
            'Requested Avg $ Price Change',
            'Final Avg $ Price Change',

            'Modeled Avg % Price Change',
            'Requested Avg % Price Change',
            'Final Avg % Price Change',

            'Modeled Wtd Avg $ Price Change',
            'Requested Wtd Avg $ Price Change',
            'Final Wtd Avg $ Price Change',

            'Modeled Wtd Avg % Price Change',
            'Requested Wtd Avg % Price Change',
            'Final Wtd Avg % Price Change',

            'ProductsModeledRevenueImpact',
            'ProductsRequestedRevenueImpact',
            'ProductsFinalRevenueImpact',
            'ServicesModeledRevenueImpact',
            'ServicesRequestedRevenueImpact',
            'ServicesFinalRevenueImpact',

            'ProductsModeledRevenueImpactPct',
            'ProductsRequestedRevenueImpactPct',
            'ProductsFinalRevenueImpactPct',
            'ServicesModeledRevenueImpactPct',
            'ServicesRequestedRevenueImpactPct',
            'ServicesFinalRevenueImpactPct',

            'ProductsModeledAverageChangePct',
            'ProductsRequestedAverageChangePct',
            'ProductsFinalAverageChangePct',
            'ServicesModeledAverageChangePct',
            'ServicesRequestedAverageChangePct',
            'ServicesFinalAverageChangePct',

            'ProductsModeledWeightedImpactChangePct',
            'ProductsRequestedWeightedImpactChangePct',
            'ProductsFinalWeightedImpactChangePct',
            'ServicesModeledWeightedImpactChangePct',
            'ServicesRequestedWeightedImpactChangePct',
            'ServicesFinalWeightedImpactChangePct',
         ],
      ].concat(
         objArray.map((item) => [
            escapeCSVValue(item.practiceName),
            escapeCSVValue(item.hm),
            escapeCSVValue(item.hmEmail),
            escapeCSVValue(item.practicePhone),
            escapeCSVValue(item.batchStatus),
            escapeCSVValue(item.discussionComplete),
            escapeCSVValue(item.scheduledFor),
            escapeCSVValue(item.currentLTMRevenue),

            //impact $
            escapeCSVValue(item.revenueImpact),
            escapeCSVValue(item.requestedRevenueImpact),
            escapeCSVValue(item.finalRevenueImpact),

            //impact %
            escapeCSVValue(item.revenueImpactPct),
            escapeCSVValue(item.requestedRevenueImpactPct),
            escapeCSVValue(item.finalRevenueImpactPct),

            //average change $
            escapeCSVValue(item.modeledAverageChange),
            escapeCSVValue(item.requestedAverageChange),
            escapeCSVValue(item.finalAverageChange),

            //average change %
            escapeCSVValue(item.modeledAverageChangePct),
            escapeCSVValue(item.requestedAverageChangePct),
            escapeCSVValue(item.finalAverageChangePct),

            //weighted change $
            escapeCSVValue(item.modeledWeightedImpactChange),
            escapeCSVValue(item.requestedWeightedImpactChange),
            escapeCSVValue(item.finalWeightedImpactChange),

            //weighted change %
            escapeCSVValue(item.modeledWeightedImpactChangePct),
            escapeCSVValue(item.requestedWeightedImpactChangePct),
            escapeCSVValue(item.finalWeightedImpactChangePct),

            //prod and svc $ impact
            escapeCSVValue(item.productsModeledRevenueImpact),
            escapeCSVValue(item.productsRequestedRevenueImpact),
            escapeCSVValue(item.productsFinalRevenueImpact),
            escapeCSVValue(item.servicesModeledRevenueImpact),
            escapeCSVValue(item.servicesRequestedRevenueImpact),
            escapeCSVValue(item.servicesFinalRevenueImpact),

            //prod and svc % impact
            escapeCSVValue(item.productsModeledRevenueImpactPct),
            escapeCSVValue(item.productsRequestedRevenueImpactPct),
            escapeCSVValue(item.productsFinalRevenueImpactPct),
            escapeCSVValue(item.servicesModeledRevenueImpactPct),
            escapeCSVValue(item.servicesRequestedRevenueImpactPct),
            escapeCSVValue(item.servicesFinalRevenueImpactPct),

            //prod and svc avg price change %
            escapeCSVValue(item.productsModeledAverageChangePct),
            escapeCSVValue(item.productsRequestedAverageChangePct),
            escapeCSVValue(item.productsFinalAverageChangePct),
            escapeCSVValue(item.servicesModeledAverageChangePct),
            escapeCSVValue(item.servicesRequestedAverageChangePct),
            escapeCSVValue(item.servicesFinalAverageChangePct),

            //prod and svc wtd avg price change %
            escapeCSVValue(item.productsModeledWeightedImpactChangePct),
            escapeCSVValue(item.productsRequestedWeightedImpactChangePct),
            escapeCSVValue(item.productsFinalWeightedImpactChangePct),
            escapeCSVValue(item.servicesModeledWeightedImpactChangePct),
            escapeCSVValue(item.servicesRequestedWeightedImpactChangePct),
            escapeCSVValue(item.servicesFinalWeightedImpactChangePct),
         ])
      )

      return array.map((row) => row.join(',')).join('\n')
   }

   hasFeedbackStarted() {
      document.getElementById('statsAdminDiv').style.display = 'none'
      return true
   }

   handleTransactionHistory(event: { practiceSKeyNumber: number; pimsId: string }): void {
      const { practiceSKeyNumber, pimsId } = event
      this.pricingService.getTransactionHistory(practiceSKeyNumber, pimsId)
   }

   toggleEdit(product: any): void {
      product.isEditing = !product.isEditing
      product.zoho = undefined
   }

   isValidPrice(value: any): boolean {
      const regex = /^\d+(\.\d+)?$/
      return regex.test(value?.toString())
   }

   uploadData(event: UploadCsvEvent): void {
      const { fileType, fileParameters, isSubmitting } = event
      switch (fileType) {
         case CsvFileType.HospitalPricing:
            this.uploadPricingData({ fileParameters, isSubmitting })
            break
         case CsvFileType.BatchUpdates:
            this.uploadBatchData({ fileParameters, isSubmitting })
            break
         case CsvFileType.RuleSets:
            this.uploadRuleSetsData({ fileParameters, isSubmitting })
            break
         case CsvFileType.Rules:
            this.uploadRulesData({ fileParameters, isSubmitting })
            break
         default:
            this.toastr.error('Unsupported file type for upload')
            break
      }
   }

   uploadPricingData(event: { fileParameters: FileParameter[]; isSubmitting: boolean }): void {
      const { fileParameters } = event
      if (!fileParameters) {
         alert('CSV file must be supplied')
      }

      this.pricingManagerClient.uploadPricingUpdatesFromCsv(fileParameters, this.pricingImportDestinationTable).subscribe({
         next: (res: any) => {
            this.onFinishUpload({ uploadSuccessful: true, message: res })
         },
         error: (err) => {
            this.onFinishUpload({ uploadSuccessful: false, message: err })
         },
      })
   }

   uploadBatchData(event: { fileParameters: FileParameter[]; isSubmitting: boolean }): void {
      const { fileParameters } = event
      if (!fileParameters) {
         alert('CSV file must be supplied')
      }

      this.pricingManagerClient.uploadBatchUpdatesFromCsv(fileParameters, this.pricingImportDestinationTable).subscribe({
         next: (res: any) => {
            this.onFinishUploadBatch({ uploadSuccessful: true, message: res })
         },
         error: (err) => {
            this.onFinishUploadBatch({ uploadSuccessful: false, message: err })
         },
      })
   }

   uploadRuleSetsData(event: { fileParameters: FileParameter[]; isSubmitting: boolean }): void {
      const { fileParameters } = event
      if (!fileParameters) {
         alert('CSV file must be supplied')
      }

      this.pricingManagerClient.uploadRuleSetUpdatesFromCsv(fileParameters, this.pricingImportDestinationTable).subscribe({
         next: (res: any) => {
            this.onFinishUpload({ uploadSuccessful: true, message: res })
         },
         error: (err) => {
            this.onFinishUpload({ uploadSuccessful: false, message: err })
         },
      })
   }

   uploadRulesData(event: { fileParameters: FileParameter[]; isSubmitting: boolean }): void {
      const { fileParameters } = event
      if (!fileParameters) {
         alert('CSV file must be supplied')
      }

      this.pricingManagerClient.uploadRulesUpdatesFromCsv(fileParameters, this.pricingImportDestinationTable).subscribe({
         next: (res: any) => {
            this.onFinishUpload({ uploadSuccessful: true, message: res })
         },
         error: (err) => {
            this.onFinishUpload({ uploadSuccessful: false, message: err })
         },
      })
   }

   validatePricesCsv(fileParameter: FileParameter, expectedCsvColumnNames: string[]): void {
      if (!fileParameter) {
         return
      }

      this.isValidating.next(true)

      this.pricingManagerClient.validateCsvPrices(fileParameter, expectedCsvColumnNames).subscribe({
         next: (res) => {
            if (res.isValid) {
               this.toastr.success('CSV is valid')
            } else {
               this.csvErrors.next(res.errors.map((err) => ({ index: fileParameter.fileName, message: err })))
               this.csvWarnings.next(res.warnings.map((warn) => ({ index: fileParameter.fileName, message: warn })))
               let message = 'CSV validation failed'
               this.toastr.error(message)
            }
         },
         error: (err) => {
            console.error(`Error uploading csv: ${err}`)
         },
         complete: () => this.isValidating.next(false),
      })
   }

   validateBatchUpdatesCsv(fileParameter: FileParameter, expectedCsvColumnNames: string[]): void {
      if (!fileParameter) {
         return
      }

      this.isValidating.next(true)

      this.pricingManagerClient.validateCsvBatch(fileParameter, expectedCsvColumnNames).subscribe({
         next: (res) => {
            if (res.isValid) {
               this.toastr.success('CSV is valid')
            } else {
               this.csvErrors.next(res.errors.map((err) => ({ index: fileParameter.fileName, message: err })))
               this.csvWarnings.next(res.warnings.map((warn) => ({ index: fileParameter.fileName, message: warn })))
               let message = 'CSV validation failed'
               this.toastr.error(message)
            }
         },
         error: (err) => {
            console.error(`Error uploading csv: ${err}`)
         },
         complete: () => this.isValidating.next(false),
      })
   }

   invalidateFile(fileParameter: FileParameter): void {
      this.csvErrors.next([{ index: fileParameter.fileName, message: 'Invalid template format' }])
      let message = 'Invalid template format'
      this.toastr.error(message)
   }

   validateRuleSetsCsv(fileParameter: FileParameter, expectedCsvColumnNames: string[]): void {
      if (!fileParameter) {
         return
      }

      this.isValidating.next(true)

      this.pricingManagerClient.valdiateCsvRulesets(fileParameter, expectedCsvColumnNames).subscribe({
         next: (res) => {
            if (res.isValid) {
               this.toastr.success('CSV is valid')
            } else {
               this.csvErrors.next(res.errors.map((err) => ({ index: fileParameter.fileName, message: err })))
               this.csvWarnings.next(res.warnings.map((warn) => ({ index: fileParameter.fileName, message: warn })))
               let message = 'CSV validation failed'
               this.toastr.error(message)
            }
         },
         error: (err) => {
            console.error(`Error uploading csv: ${err}`)
         },
         complete: () => this.isValidating.next(false),
      })
   }

   validateRulesCsv(fileParameter: FileParameter, expectedCsvColumnNames: string[]): void {
      if (!fileParameter) {
         return
      }

      this.isValidating.next(true)

      this.pricingManagerClient.valdiateCsvRules(fileParameter, expectedCsvColumnNames).subscribe({
         next: (res) => {
            if (res.isValid) {
               this.toastr.success('CSV is valid')
            } else {
               this.csvErrors.next(res.errors.map((err) => ({ index: fileParameter.fileName, message: err })))
               this.csvWarnings.next(res.warnings.map((warn) => ({ index: fileParameter.fileName, message: warn })))
               let message = 'CSV validation failed'
               this.toastr.error(message)
            }
         },
         error: (err) => {
            console.error(`Error uploading csv: ${err}`)
         },
         complete: () => this.isValidating.next(false),
      })
   }

   showUploadPricingCsvModal(): void {
      this.bsModalService.show(VetcorUploadCsvModalComponent, {
         class: 'modal-xl custom-modal pricing-big-modal modal-padding',
         backdrop: 'static',
         initialState: {
            isSubmittingCsv: this.isSubmittingCsv,
            isSubmittingDbx: this.isSubmittingDbx,
            onUploadCsv: this.onUploadCsv,
            onSelectCsv: this.onSelectFile,
            errorMessages: this.csvErrors,
            warningMessages: this.csvWarnings,
            isValidating: this.isValidating,
         },
      })
   }

   onFinishUpload(uploadEvent: { uploadSuccessful: boolean; message: { data: Blob } }) {
      this.isSubmittingCsv.next(false)
      const { uploadSuccessful, message } = uploadEvent
      if (!uploadSuccessful) {
         alert(`Upload failed.  Reason: ${message}`)
      } else {
         message.data.text().then((messageText) => this.toastr.success(messageText))
      }
   }

   onFinishUploadBatch(uploadEvent: { uploadSuccessful: boolean; message: { data: Blob } }) {
      this.isSubmittingCsv.next(false)
      const { uploadSuccessful, message } = uploadEvent
      if (!uploadSuccessful) {
         this.toastr.error(`Upload failed.  Reason: ${message}`)
      } else {
         message.data.text().then((messageText) => this.toastr.success(messageText))
      }
   }

   uploadPricingUpdatesViaADF(source: string, pipeline: string): void {
      this.authManager
         .getCurrentUser()
         .pipe(
            tap((user) => user),
            catchError((err) => {
               this.toastr.error('Could not determine current user.  Pricing update not submitted.')
               console.error('Error fetching current user for ADF pricing update:', err)
               return EMPTY
            }),
            switchMap((user) => of(user.email)),
            switchMap((email) =>
               this.pricingManagerClient.sendPricingUpdateJob({ pipeline, source, deleteExisting: false, user: email }).pipe(
                  tap(() => {
                     this.toastr.success(`Successfully submitted job.  You will receive an email when complete.`)
                     this.isSubmittingDbx.next({ source: '', pipeline: '' })
                  }),
                  catchError((err) => {
                     console.error('Error submitting ADF pricing update job:', err)
                     this.toastr.error(`Error running DBX job: ${err.message || err}`)
                     this.isSubmittingDbx.next({ source: '', pipeline: '' })
                     throw err
                  })
               )
            )
         )
         .subscribe()
   }

   showDBXModal(): void {
      this.bsModalService.show(VetcorUploadDbxModalComponent, {
         class: 'modal-xl custom-modal pricing-big-modal modal-padding',
         backdrop: 'static',
         initialState: {
            isSubmitting: this.isSubmittingDbx,
         },
      })
   }
}
